import { useState } from 'react';
import { Flag } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ReportOfferModal from './ReportOfferModal';
import { AppButton } from '../../../shared/components/buttons/AppButton';
import { Box } from '@mui/system';

interface Props {
  offerId?: string;
}

export default function ReportOfferButton({ offerId }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const handleReported = () => {
    setConfirmationOpen(true);
  };

  return (
    <>
      <Stack spacing={1} sx={{ mt: 1 }}>
        <AppButton
          variant="outlined"
          fullWidth
          startIcon={<Flag />}
          onClick={() => setOpen(true)}
          data-testid="report-offer-button"
        >
          {t('offerDetail.reportButton')}
        </AppButton>
      </Stack>

      <ReportOfferModal
        open={open}
        onClose={() => setOpen(false)}
        offerId={offerId}
        onReported={handleReported}
      />

      {/* Confirmation drawer */}
      <ReportOfferModalConfirmation
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
      />
    </>
  );
}

function ReportOfferModalConfirmation({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Box>
      {/* lightweight confirmation using Drawer for consistency */}
      {/* Inline implementation to avoid another file */}
      {open && (
        <div>
          {/* Backdrop */}
          <div
            role="presentation"
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1399,
            }}
            onClick={onClose}
            onKeyDown={e => {
              if (e.key === 'Escape') onClose();
            }}
          />
          {/* Use a simple modal-like UI */}
          <div
            style={{
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1400,
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                width: 360,
                background: 'white',
                borderRadius: 12,
                padding: 18,
                boxShadow: '0 6px 18px rgba(0,0,0,0.2)',
                pointerEvents: 'auto',
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                {t('offerDetail.reportConfirmationTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('offerDetail.reportConfirmationMessage')}
              </Typography>
              <AppButton variant="primary" fullWidth onClick={onClose}>
                {t('offerDetail.reportConfirmationClose')}
              </AppButton>
            </div>
          </div>
        </div>
      )}
    </Box>
  );
}
